import { useEffect, useState } from "react";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import { ActionsMenuEntry } from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import { useFeatureToggle } from "../external/ensolvers-core-frontend-mui/hooks/useFeatureToggle";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import { trace } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import { Documentation } from "../models/Documentation";
import Timeoff from "../models/Timeoff";
import WorkLog from "../models/WorkLog";
import DocumentationService from "../services/DocumentationService";
import PositionService from "../services/PositionService";
import ProjectService from "../services/ProjectService";
import timeoffService from "../services/TimeoffService";
import UserService from "../services/UserService";
import WorkLogService from "../services/WorkLogService";
import { TimeOffStatus } from "../utils/enums/TimeOffStatus";
import { Features } from "../utils/Features";
import OpsDateUtils from "../utils/OpsDateUtils";
import RoleUtils from "../utils/RoleUtils";
import TimeOffUtils from "../utils/TimeOffUtils";
import featureToggleList from "../utils/toggleConfig.json";
import { useHistory, useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import { useTranslation } from "react-i18next";

import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import RuleIcon from '@mui/icons-material/Rule';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

const ONE_MEGABYTE = 1000000;

const useTimeOffList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [timeoff, setTimeoff] = useState<Timeoff | undefined>();
  const [viewTimeoff, setViewTimeoff] = useState<Timeoff | undefined>();
  const [userCanResolveRequest, setUserCanResolveRequest] = useState(false);
  const [userExternalId, setUserExternalId] = useState<string>();
  const [update, setUpdate] = useState<number | undefined>(undefined);
  const [selectedTimeOff, setSelectedTimeOff] = useState<Timeoff | undefined>(undefined);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [dateFilter, setDateFilter] = useState<string>("Upcoming");
  const [selectedFiles, setSelectedFiles] = useState<Documentation[]>([]);
  const [usernames, setUsernames] = useState<AutoCompleteUIDTO[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [cancelRequestTimeOff, setCancelRequestTimeOff] = useState<Timeoff | undefined>(undefined);
  const [editTimeoff, setEditTimeoff] = useState<Timeoff | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [createForOthers, setCreateForOthers] = useState<boolean>(false);
  const [projects, setProjects] = useState<AutoCompleteUIDTO[]>([]);
  const [projectSearch, setProjectSearch] = useState<AutoCompleteUIDTO | null>(null);
  const [isGanttGraphEnabled] = useFeatureToggle(featureToggleList, Features.GANTT_GRAPH);

  const searchParams = new URLSearchParams(useLocation().search);
  const status = searchParams.get("status");
  const queryDate = searchParams.get("date");
  const queryYear = searchParams.get("year");
  const queryMonth = searchParams.get("month");
  const search = searchParams.get("search");
  const project = searchParams.get("project");

  useEffect(() => {
    if (status === null) {
      searchParams.set("status", "All");
    } else {
      setStatusFilter(status);
    }

    if (queryDate === null) {
      searchParams.set("date", "Upcoming");
    } else {
      setDateFilter(queryDate);
    }

    if (Routes.TIME_OFF === window.location.pathname) history.replace({ pathname: Routes.TIME_OFF, search: searchParams.toString() });
  }, []);

  useEffect(() => {
    setUserCanResolveRequest(RoleUtils.isAdminOrManager());
    UserService.findByCoreUserId(AuthService.getUser()?.externalId!)
      .then((response) => setUserExternalId(response.getContentOrThrowError().externalId))
      .catch((error) => trace("Error getting usernames", error));

    if (RoleUtils.isAdmin()) {
      ProjectService.findProjectNames()
        .then((result) => {
          setProjects(result.getContent());
        })
        .catch((error) => trace("Error getting project names", error));
      UserService.findUserNames()
        .then((response) => setUsernames(response.getContentOrThrowError()))
        .catch((error) => trace("Error getting usernames", error));
    } else if (RoleUtils.isManager()) {
      PositionService.findProjectUserListFromManager()
        .then((response) => setUsernames(response.getContent()))
        .catch((error) => trace("Error getting usernames", error));
      ProjectService.findProjectsNamesByUserManagerExternalId()
        .then((response) => setProjects(response.getContent()))
        .catch((error) => trace("Error getting usernames", error));
    }
  }, []);

  useEffect(() => {
    if (projects && project !== null) {
      const queryProject = projects.find((p) => p.externalId === project);
      if (queryProject) setProjectSearch(queryProject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  const createTimeOff = (timeOff: Timeoff) => {
    setIsLoading(true);
    timeOff.shortStartDate = OpsDateUtils.toUTCWithSetHours(timeOff.shortStartDate);
    timeOff.shortEndDate = OpsDateUtils.toUTCWithSetHours(timeOff.shortEndDate);

    return timeoffService
      .createTimeOff(timeOff, selectedFiles, AuthService.getUser()?.externalId!)
      .then(() => {
        setSelectedFiles([]);
        timeOff.requesterExternalId = timeOff.requesterExternalId ? timeOff.requesterExternalId : AuthService.getUser()?.externalId!;
        getUserWorkLogs(timeOff.requesterExternalId, timeOff.shortStartDate, timeOff.shortEndDate)
          .then((response) => {
            if (response.length > 0) {
              NotificationService.warn(
                "Operation successful. There is at least one worklog created for the same period. Please update it accordingly."
              );
            } else {
              NotificationService.info("TimeOff created successfully");
            }
            setIsLoading(false);
          })
          .catch(() => {
            NotificationService.error("Error fetching user worklogs for selected days");
            setIsLoading(false);
          });
      })
      .catch((error) => {
        NotificationService.error(error.response.data.message);
        setIsLoading(false);
      });
  };

  const updateTimeOff = (timeOff: Timeoff) => {
    timeOff.status = timeOff.status.replaceAll(" ", "_");
    return timeoffService
      .updateTimeOff(timeOff, selectedFiles)
      .then(() => {
        setSelectedFiles([]);
        getUserWorkLogs(timeOff.requesterExternalId, timeOff.shortStartDate, timeOff.shortEndDate)
          .then((response) => {
            if (response.length > 0) {
              NotificationService.warn(
                "Operation successful. There is at least one worklog created for the same period. Please update it accordingly."
              );
            } else {
              NotificationService.info("TimeOff updated successfully");
            }
          })
          .catch(() => NotificationService.error("Error fetching user worklogs for selected days"));
      })
      .catch(() => NotificationService.error("Error updating timeOff"));
  };

  const getTimeOff = async (
    page: number,
    pageSize: number,
    gantt: boolean = false,
    year: Date | null,
    month: Date | null
  ): Promise<PagedResult<Timeoff>> => {
    const searchValue = projectSearch !== null && project !== null ? projectSearch.externalId : "All";

    const timeOffList: PagedResult<Timeoff> = (
      await timeoffService.getTimeOff(
        page,
        pageSize,
        searchFilter,
        statusFilter,
        gantt ? "Upcoming" : dateFilter,
        project ? project : searchValue,
        year?.getFullYear(),
        month?.getMonth()
      )
    ).getContent();
    timeOffList.content.forEach((timeoff: Timeoff) => {
      timeoff.shortStartDate = OpsDateUtils.utcOffset(timeoff.shortStartDate);
      timeoff.shortEndDate = OpsDateUtils.utcOffset(timeoff.shortEndDate);
    });
    return timeOffList;
  };

  const handleApprovalTimeOff = (data: Timeoff, status: string) => {
    if (!timeoff) return;
    timeoff.statusReason = data.statusReason;
    handleOnChangeTimeOffStatus(timeoff, status);
  };

  const handleOnCancelTimeOff = (data: Timeoff, status: string) => {
    if (!selectedTimeOff) return;
    selectedTimeOff.statusReason = data.statusReason;
    handleOnChangeTimeOffStatus(selectedTimeOff, status);
  };

  const handleOnChangeTimeOffStatus = async (data: Timeoff, status: string) => {
    data.status = data.status.replaceAll(" ", "_");
    (await timeoffService.changeStatus(data, status))
      .onSuccess(() => {
        setTimeoff(undefined);
        setSelectedTimeOff(undefined);
        setCancelRequestTimeOff(undefined);
        setSelectedFiles([]);

        if (status === TimeOffStatus.APPROVED) {
          TimeOffUtils.createTimeOffWorklogs(data);
        }

        if (status === TimeOffStatus.CANCELLED) {
          TimeOffUtils.deleteTimeOffWorklogs(data);
        }

        // When reset timeoff
        if (data.approvedBy && status === TimeOffStatus.PENDING) {
          TimeOffUtils.deleteTimeOffWorklogs(data);
        }

        if (status !== TimeOffStatus.CANCELLED && status !== TimeOffStatus.APPROVED) {
          NotificationService.info("Status changed successfully");
        }
      })
      .onError((response) => {
        NotificationService.error(response.getContent());
        setTimeoff(undefined);
        setSelectedTimeOff(undefined);
        setCancelRequestTimeOff(undefined);
        setSelectedFiles([]);
      });
    await getUserWorkLogs(data.requesterExternalId, data.shortStartDate, data.shortEndDate);
  };

  const resetTimeOff = async (timeOff: Timeoff, status: string) => {
    timeOff.status = timeOff.status.replaceAll(" ", "_");
    (await timeoffService.changeStatus(timeOff, status))
      .onSuccess(() => {
        setUpdate(Date.now());
        setSelectedFiles([]);
        timeOff.status === TimeOffStatus.APPROVED
          ? TimeOffUtils.deleteTimeOffWorklogs(timeOff)
          : NotificationService.info("Status reset successfully");
      })
      .onError((response) => {
        NotificationService.error(response.getContent());
        setSelectedFiles([]);
      });
  };

  const handleUploadDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const fileList = e.target.files;

    if (!fileList) return;

    let files: Documentation[] = selectedFiles;
    let newDocument: Documentation;
    let fileToUpload: File = fileList[0];

    if (fileToUpload.size > ONE_MEGABYTE) {
      NotificationService.error("The maximum size to upload is 1MB.");
      setIsLoading(false);
      return;
    }

    (await DocumentationService.uploadDocument({ file: fileToUpload, name: fileToUpload.name }))
      .onSuccess((response) => {
        newDocument = response.getContent();
        files?.push(newDocument);
        setIsLoading(false);
      })
      .onError((error) => {
        NotificationService.error(error.getContent());
        setIsLoading(false);
      });

    setSelectedFiles(files);
    setUpdate(Date.now);
    setIsLoading(false);
  };

  const handleDeleteDocumentIcon = (files: Documentation[]) => {
    setSelectedFiles(files);
    setUpdate(Date.now);
  };

  const getAdditionalActions = (instance: Timeoff): ActionsMenuEntry[] => {
    let options = Array<ActionsMenuEntry>();

    if (TimeOffUtils.userCanOpenOrCancel(instance, userExternalId) && TimeOffUtils.timeOffCanBeCancelled(instance)) {
      options.push({
        title: "Cancel",
        action: () => setSelectedTimeOff(instance),
      });
    }

    if (instance.status === TimeOffStatus.CANCELLED && TimeOffUtils.userCanOpenOrCancel(instance, userExternalId)) {
      options.push({
        title: "Reopen",
        action: () => setSelectedTimeOff(instance),
      });
    }

    if (
      RoleUtils.isAdminOrManager() &&
      TimeOffUtils.userCanChangeStatus(instance, userExternalId) &&
      instance.status.replace("_", " ") === TimeOffStatus.PENDING_CANCELLATION
    ) {
      options.push({
        title: "Approve / Reject",
        action: () => setCancelRequestTimeOff(instance),
      });
    }

    if (TimeOffUtils.userCanChangeStatus(instance, userExternalId)) {
      options.push(
        instance.status === TimeOffStatus.PENDING || instance.status.replace("_", " ") === TimeOffStatus.ADD_DOCUMENTATION
          ? {
              title: "Approve / Reject",
              action: () => setTimeoff(instance),
            }
          : {
              title: "Reset status",
              action: () => resetTimeOff(instance, TimeOffStatus.PENDING),
            }
      );
    }

    if (TimeOffUtils.timeOffCanBeEdited(instance)) {
      options.push({
        title: "Edit",
        action: () => openEditDialog(instance),
      });
    }
    return options;
  };

  const getUserWorkLogs = async (userExternalId: string, startDate: Date, endDate: Date) => {
    let userWorklogs: WorkLog[] = [];
    await WorkLogService.findWorkLogsByRequesterAndDates(
      userExternalId,
      DateUtils.formatDate(startDate, "YYYY-MM-DD"),
      DateUtils.formatDate(endDate, "YYYY-MM-DD")
    ).then((response) => {
      userWorklogs = response.getContent();
    });

    return userWorklogs;
  };

  const handleMissingDocumentationRequested = (instance: Timeoff) => {
    return instance.status.replace("_", " ") === TimeOffStatus.ADD_DOCUMENTATION && instance.documentation.length === 0;
  };

  const openViewDialog = (instance: Timeoff) => {
    setViewTimeoff(instance);
    setSelectedFiles(instance.documentation);
  };

  const openEditDialog = (instance: Timeoff) => {
    setEditTimeoff(instance);
    setSelectedFiles(instance.documentation);
  };

  const closeViewDialog = () => {
    setSelectedFiles([]);
    setViewTimeoff(undefined);
  };

  const closeEditDialog = () => {
    setSelectedFiles([]);
    setEditTimeoff(undefined);
  };

  const clearSelectedFiles = () => setSelectedFiles([]);
  const clearTimeoff = () => setTimeoff(undefined);
  const clearSelectedTimeoff = () => setSelectedTimeOff(undefined);

  const getAdditionalActionsIcons = (instance: Timeoff): ActionsMenuEntryIcons[] => {
    const actions = []

    if (TimeOffUtils.userCanOpenOrCancel(instance, userExternalId) && TimeOffUtils.timeOffCanBeCancelled(instance)) {
      actions.push(
        {
          title: t("Cancel"),
          action: () => setSelectedTimeOff(instance),
          icon:<CancelIcon/>
        },        
      )
    }
    if (instance.status === TimeOffStatus.CANCELLED && TimeOffUtils.userCanOpenOrCancel(instance, userExternalId)) {
      actions.push(
        {
          title: t("Reopen"),
          action: () => setSelectedTimeOff(instance),
          icon:<EventRepeatIcon/>
        },      
      )
    }

    if (
      RoleUtils.isAdminOrManager() &&
      TimeOffUtils.userCanChangeStatus(instance, userExternalId) &&
      instance.status.replace("_", " ") === TimeOffStatus.PENDING_CANCELLATION
    ) {
      actions.push(
        {
          title: "Approve / Reject",
          action: () => setCancelRequestTimeOff(instance),
          icon:<RuleIcon/>
        },   
      )
    }

    if (TimeOffUtils.userCanChangeStatus(instance, userExternalId)) {
      actions.push(
        {
          title: "Approve / Reject",
          action: () => setTimeoff(instance),
          icon:<RuleIcon/>
        },
        {
          title: "Reset status",
          action: () => resetTimeOff(instance, TimeOffStatus.PENDING),
          icon:<RestartAltIcon/>
        },  
      )
    }

    if (TimeOffUtils.timeOffCanBeEdited(instance)) {
      actions.push(
        {
          title: t("Edit"),
          action: () => openEditDialog(instance),
          icon:<EditIcon/>
        },    
      )
    }
    actions.push(
      {
        title: t("View"),
        action: () => openEditDialog(instance),
        icon:<VisibilityIcon/>
      },
    )
    return actions;
  };  

  return {
    timeoffState: {
      timeoff,
      getTimeOff,
      createTimeOff,
      clearTimeoff,
      updateTimeOff,
      selectedTimeOff,
      editTimeoff,
      clearSelectedTimeoff,
      viewTimeoff,
      handleOnChangeTimeOffStatus,
      cancelRequestTimeOff,
      setCancelRequestTimeOff,
      handleApprovalTimeOff,
      handleOnCancelTimeOff,
    },
    clearSelectedFiles,
    handleUploadDocument,
    handleDeleteDocumentIcon,
    getAdditionalActions,
    getAdditionalActionsIcons,
    openViewDialog,
    closeViewDialog,
    closeEditDialog,
    setSelectedFiles,
    isEditing,
    usernames,
    userExternalId,
    selectedFiles,
    isLoading,
    openCreateModal,
    setOpenCreateModal,
    createForOthers,
    setCreateForOthers,
    userCanResolveRequest,
    setIsEditing,
    update,
    searchFilter,
    statusFilter,
    dateFilter,
    projects,
    projectSearch,
    setSearchFilter,
    setStatusFilter,
    setDateFilter,
    setProjectSearch,
    getUserWorkLogs,
    handleMissingDocumentationRequested,
    isGanttGraphEnabled,
    handleOnChangeTimeOffStatus,
    status,
    queryDate,
    queryYear,
    queryMonth,
    search,
    project,
  };
};

export default useTimeOffList;
